"use client"

import React, { useActionState, useEffect, useRef, useState } from "react"
import { useRouter } from "next/navigation"
import { deleteBrandingImageAction, uploadBrandingImageAction } from "@/actions/ociSettingsActions"
import { FolderOpen, ImageUp, LoaderCircle, PlusIcon, SaveIcon, TrashIcon } from "lucide-react"
import { useTranslations } from "next-intl"
import { useDropzone } from "react-dropzone"

import { ACCEPTED_BRANDING_FILE_TYPES, ACCEPTED_BRANDING_MAX_FILE_SIZE } from "@/lib/oci-constants"
import { cn } from "@/lib/utils"
import { Button } from "@/components/ui/components"
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/components/alert-dialog"

interface AccountOciBrandingClientProps extends Omit<React.HTMLProps<HTMLDivElement>, "children"> {
  label_title?: string
  label_subtitle?: string
  label_image_preview: string
  label_drag_n_drop: string
  label_btn_open_file_dialog: string
  label_btn_submit: string
  label_btn_delete: string
  label_btn_reset: string
  label_error_file_required: string
  label_error_file_type_invalid: string
  label_error_file_size_invalid: string
  label_error_upload: string
  label_error_delete: string
  label_current_header: string
  label_current_mobile: string
  label_current_desktop: string
  brandingImage: string | undefined
  alertDeleteImageTitle: string
  alertDeleteImageDescription: string
}

export const AccountOciBrandingClient: React.FC<AccountOciBrandingClientProps> = ({
  className,
  label_title,
  label_subtitle,
  label_image_preview,
  label_drag_n_drop,
  label_btn_open_file_dialog,
  label_btn_submit,
  label_btn_delete,
  label_btn_reset,
  label_error_file_required,
  label_error_file_type_invalid,
  label_error_file_size_invalid,
  label_error_upload,
  label_error_delete,
  label_current_header,
  label_current_mobile,
  label_current_desktop,
  brandingImage,
  alertDeleteImageTitle,
  alertDeleteImageDescription,
  ...restProps
}) => {
  const t = useTranslations("storefront")
  const [preview, setPreview] = useState<string>("")
  const [dropzoneError, setDropzoneError] = useState<string>("")
  const [state, formAction, isPending] = useActionState(reloadPageAfterSuccessfulUpload, { message: "" })
  const inputRef = useRef<HTMLInputElement>(null)
  const router = useRouter()
  const { getRootProps, getInputProps, open } = useDropzone({
    multiple: false,
    maxFiles: 1,
    maxSize: ACCEPTED_BRANDING_MAX_FILE_SIZE,
    accept: Object.fromEntries(ACCEPTED_BRANDING_FILE_TYPES.map((t) => [t, []])),
    onDrop: (incomingFiles, fileRejections) => {
      setDropzoneError("")
      fileRejections.forEach((fileRejection) => {
        fileRejection.errors.forEach((error) => {
          if (error.code === "file-too-large" || error.code === "file-too-small") {
            setDropzoneError("FILE_SIZE_INVALID")
          } else if (error.code === "file-invalid-type") {
            setDropzoneError("FILE_TYPE_INVALID")
          }
        })
      })
      if (incomingFiles.length > 0) {
        setPreview(URL.createObjectURL(incomingFiles[0]))
        if (inputRef.current) {
          const dT = new DataTransfer()
          incomingFiles.forEach((v) => {
            dT.items.add(v)
          })
          inputRef.current.files = dT.files
        }
      }
    },
    disabled: isPending,
  })

  async function reloadPageAfterSuccessfulUpload(state: any, formData: FormData): Promise<{ message: string }> {
    const result = await uploadBrandingImageAction(state, formData)
    if (result.message == "") {
      reset()
      router.refresh()
    }
    return result
  }

  function reset() {
    setPreview("")
    setDropzoneError("")
  }

  useEffect(() => {
    return () => URL.revokeObjectURL(preview)
  }, [preview])

  return (
    <div className={"flex flex-col gap-4"}>
      <div className={"flex flex-row justify-between gap-2"}>
        <div>
          <span className={"text-2xl font-semibold"}>{label_title}</span>
          <p>{label_subtitle}</p>
        </div>
      </div>
      <div className={"w-full"}>
        {brandingImage && (
          <>
            <div className={"mt-4 flex flex-col gap-4"}>
              <span className={"text-lg font-semibold"}>{label_current_header}</span>
              <div className={"flex flex-row flex-wrap gap-4"}>
                <div className={"flex flex-col gap-2"}>
                  <span className={"text-base font-semibold"}>{label_current_desktop}</span>
                  <div
                    className={`
                      flex aspect-256/70 h-auto w-64 shrink-0 grow-0 flex-col items-center justify-center gap-x-1
                      overflow-hidden bg-white
                    `}
                  >
                    {/* eslint-disable-next-line @next/next/no-img-element */}
                    <img src={brandingImage} alt="preview" className={"size-full object-contain object-bottom-left"} />
                  </div>
                </div>
                <div className={"flex flex-col gap-2"}>
                  <span className={"text-base font-semibold"}>{label_current_mobile}</span>
                  <div
                    className={`
                      flex aspect-427/78 h-auto w-36 shrink-0 grow-0 flex-row items-center justify-center gap-x-1
                      overflow-hidden bg-white
                    `}
                  >
                    {/* eslint-disable-next-line @next/next/no-img-element */}
                    <img src={brandingImage} alt="preview" className={"size-full object-contain object-bottom-left"} />
                  </div>
                </div>
              </div>
              <div className={"flex"}>
                <AlertDialog>
                  <AlertDialogTrigger
                    className={`
                      inline-flex h-9 items-center justify-center gap-1 rounded-lg bg-destructive px-4 py-2 text-sm
                      font-bold whitespace-nowrap text-destructive-foreground shadow-xs transition-colors
                      group-hover:bg-destructive/90
                      hover:bg-destructive/90
                      focus-visible:ring-1 focus-visible:ring-ring focus-visible:outline-hidden
                      disabled:pointer-events-none disabled:opacity-50
                      3xl:text-lg
                    `}
                    disabled={isPending}
                  >
                    <TrashIcon className={"inline size-5"} />
                    <span>{label_btn_delete}</span>
                  </AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle>{alertDeleteImageTitle}</AlertDialogTitle>
                      <AlertDialogDescription>{alertDeleteImageDescription}</AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel>{t("common.cancel")}</AlertDialogCancel>
                      <AlertDialogAction
                        onClick={() =>
                          deleteBrandingImageAction().then((r) => {
                            if (r.message == "") {
                              reset()
                              router.refresh()
                            } else {
                              setDropzoneError(r.message)
                            }
                          })
                        }
                      >
                        {t("common.confirm")}
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              </div>
            </div>
            <hr className={"m-4"} />
          </>
        )}
        <form action={formAction}>
          <div className={cn("@container flex w-full flex-col gap-4", className)} {...restProps}>
            <div
              {...getRootProps({
                className: cn(
                  `
                    flex cursor-pointer flex-col items-center justify-center gap-2 rounded-sm border border-gray-200
                    bg-gray-100 p-4
                    hover:border-gray-300 hover:bg-gray-200
                  `
                ),
              })}
            >
              <div className={cn("size-fit border-2 border-dashed border-gray-400 bg-white")}>
                <div
                  className={`
                    flex aspect-427/78 h-auto w-36 shrink-0 grow-0 flex-row items-center justify-center gap-x-1
                    overflow-hidden
                    lg:aspect-256/70 lg:w-64 lg:flex-col
                  `}
                >
                  {preview != "" ? (
                    // eslint-disable-next-line @next/next/no-img-element
                    <img
                      src={preview}
                      alt="preview"
                      className={"size-full object-contain object-bottom-left"}
                      onLoad={() => URL.revokeObjectURL(preview)}
                    />
                  ) : (
                    <>
                      <ImageUp className={"h-5 w-fit text-gray-400 lg:h-10"} />
                      <span className={"text-xs font-light"}>{label_image_preview}</span>
                    </>
                  )}
                </div>
              </div>
              <input {...getInputProps()} />
              <p className={"w-fit"}>{label_drag_n_drop}</p>
              <div className={"size-fit"}>
                <Button
                  onClick={(e) => {
                    e.stopPropagation()
                    e.preventDefault()
                    open()
                  }}
                  size={"smNoGrow"}
                  variant={"default"}
                  className={"gap-1"}
                >
                  <FolderOpen className={"inline size-5"}></FolderOpen>
                  <span>{label_btn_open_file_dialog}</span>
                </Button>
              </div>
            </div>
            {(state.message || dropzoneError != "") && (
              <p className={"rounded-sm border border-gray-400 bg-white p-2 text-center text-red-600"} role="alert">
                {state.message == "FILE_REQUIRED" && label_error_file_required}
                {(state.message == "FILE_TYPE_INVALID" || dropzoneError == "FILE_TYPE_INVALID") &&
                  label_error_file_type_invalid}
                {(state.message == "FILE_SIZE_INVALID" || dropzoneError == "FILE_SIZE_INVALID") &&
                  label_error_file_size_invalid}
                {state.message == "FILE_UPLOAD_ERROR" && label_error_upload}
                {dropzoneError == "FILE_DELETE_ERROR" && label_error_delete}
              </p>
            )}
            <input type={"file"} name={"image"} required className={"size-px opacity-0"} ref={inputRef} />
            <div className="flex flex-wrap justify-between gap-4 max-lg:flex-col-reverse">
              <Button
                type={"reset"}
                variant="outline"
                size={"defaultNoGrow"}
                className={"gap-1"}
                disabled={isPending}
                onClick={reset}
              >
                <PlusIcon className={"inline size-5 rotate-45"} />
                <span>{label_btn_reset}</span>
              </Button>

              <Button type={"submit"} size={"defaultNoGrow"} disabled={isPending} className={"gap-1"}>
                {!isPending ? (
                  <SaveIcon className={"inline size-5"} />
                ) : (
                  <LoaderCircle className={"inline size-5 motion-safe:animate-spin"} />
                )}
                <span>{label_btn_submit}</span>
              </Button>
            </div>
          </div>
        </form>
      </div>
    </div>
  )
}
